/**
 * Contact Service
 * 
 * Responsible for contact-related API operations.
 * Follows Single Responsibility Principle.
 */

import { Logger } from '../utils/logger.js';
import { FetchUtils } from '../utils/fetch-utils.js';
import { EmailExtractor } from '../extractors/email-extractor.js';

/**
 * Validates and normalizes contact data
 */
class ContactDataValidator {
  static validate(emails) {
    const normalizedEmails = EmailExtractor.normalizeEmails(emails);
    Logger.debug('normalized unique emails:', normalizedEmails);

    if (normalizedEmails.length === 0) {
      Logger.debug('empty email list after normalization, skipping request.');
      return { valid: false, normalizedEmails: [] };
    }

    return { valid: true, normalizedEmails };
  }
}

/**
 * Extracts essential contact data for API requests
 */
class ContactDataExtractor {
  /**
   * Extract essential contact data for API requests, removing heavy data like activities
   */
  static extract(contacts) {
    if (!contacts) {
      return contacts;
    }
    
    // Handle single contact
    if (!Array.isArray(contacts)) {
      return this.extractSingleContact(contacts);
    }
    
    // Handle array of contacts
    return contacts.map(contact => this.extractSingleContact(contact));
  }
  
  /**
   * Extract essential data from a single contact
   */
  static extractSingleContact(contact) {
    if (!contact || typeof contact !== 'object') {
      return contact;
    }
    
    // Extract only essential fields needed for email linking
    const essentialContact = {
      _id: contact._id,
      value: contact.value,
      emails: contact.emails,
      firstName: contact.firstName,
      lastName: contact.lastName,
      customers: contact.customers,
      // Keep any other small essential fields but exclude heavy data
      ...(contact.phone && { phone: contact.phone }),
      ...(contact.position && { position: contact.position }),
      ...(contact.department && { department: contact.department })
    };
    
    // Explicitly exclude heavy data that can cause "request body too large" errors
    // activities, notes, documents, etc. are not included
    
    Logger.debug('Contact data extracted:', {
      originalSize: JSON.stringify(contact).length,
      extractedSize: JSON.stringify(essentialContact).length,
      contactId: contact._id
    });
    
    return essentialContact;
  }
}

/**
 * Main Contact Service
 * Follows Single Responsibility Principle and Dependency Inversion Principle
 */
export class ContactService {
  /**
   * Fetch contacts by email addresses
   */
  static async fetchByEmails(emails) {
    Logger.debug('fetchContactsByEmail called with:', emails);
    Logger.debug('emails parameter type:', typeof emails, 'isArray:', Array.isArray(emails));
    
    // Validate and normalize emails
    const { valid, normalizedEmails } = ContactDataValidator.validate(emails);
    
    if (!valid) {
      return { contacts: [] };
    }
    
    return FetchUtils.post('/api/contact/getCustomized', {
      filter: { emails: normalizedEmails }
    });
  }
  
  /**
   * Search all contacts by text query
   * @param {string} textSearch - Search query to find contacts
   * @param {number} limit - Maximum number of results (default: 50)
   * @param {AbortSignal} signal - Optional abort signal to cancel the request
   * @returns {Promise<{contacts: Array}>} - Search results
   */
  static async searchContacts(textSearch, limit = 50, signal = null) {
    Logger.debug('searchContacts called with:', textSearch, 'limit:', limit);
    
    if (!textSearch || textSearch.trim().length === 0) {
      Logger.debug('Empty search query, returning empty results');
      return { contacts: [] };
    }
    
    return FetchUtils.post('/api/contact/getCustomized', {
      filter: {},
      textSearch: textSearch.trim(),
      limit: limit
    }, signal);
  }
  
  /**
   * Extract essential contact data for API requests
   */
  static extractContactData(contacts) {
    return ContactDataExtractor.extract(contacts);
  }
}

